import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  // Close menus on page navigation
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);


  const navLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    {
      name: 'Deposits',
      path: '/deposits',
      dropdown: [
        { name: 'Savings Deposit Account', path: '/deposits#savings-dep' },
        { name: 'Fixed Deposit (FD)', path: '/deposits#fixed-dep' },
        { name: 'Recurring Deposit (RD)', path: '/deposits#recurring-dep' },
        { name: 'Senior Citizen Deposit', path: '/deposits#senior-dep' },
        { name: 'Women Savings Scheme', path: '/deposits#women-dep' }
      ]
    },
    {
      name: 'Loans',
      path: '/loans',
      dropdown: [
        { name: 'Personal Loan', path: '/loans#personal' },
        { name: 'Home Loan', path: '/loans#home' },
        { name: 'Gold Loan', path: '/loans#gold' },
        { name: 'Vehicle Loan', path: '/loans#vehicle' },
        { name: 'Business Loan', path: '/loans#business' }
      ]
    },
    { name: 'Careers', path: '/careers' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'News & Notices', path: '/news' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white shadow-lg py-4">
      <div className="w-full px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img src="/Sahyadri Urban Logo.png" alt="Sahyadri Urban Logo" className="h-8 sm:h-9 w-auto object-contain" />
          </Link>

          {/* Desktop Nav Links & Actions */}
          <div className="hidden xl:flex items-center space-x-4 ml-auto">
            {/* Desktop Nav Links */}
            <div className="flex items-center space-x-3.5">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative group whitespace-nowrap"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={link.path}
                    className={`flex items-center px-2 py-2 text-sm font-semibold transition-colors font-poppins ${location.pathname === link.path ? 'text-[#B31825] border-b-2 border-[#B31825]' : 'text-[#052046] hover:text-[#B31825]'
                      }`}
                  >
                    <span>{link.name}</span>
                    {link.dropdown && (
                      <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180 text-gray-500" />
                    )}
                  </Link>

                  {/* Dropdown Menu */}
                  {link.dropdown && activeDropdown === link.name && (
                    <div className="absolute top-full left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl mt-1 py-2 overflow-hidden z-50 transition-all duration-200 animate-fade-in">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                        <span className="text-xs font-bold text-[#B31825] uppercase tracking-wider">{link.name} Portfolio</span>
                      </div>
                      {link.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block px-4 py-2.5 text-sm text-[#052046] hover:text-white hover:bg-[#B31825] transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>


          </div>

          {/* Mobile hamburger menu toggle */}
          <div className="xl:hidden flex items-center space-x-3">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-[#052046] hover:text-[#B31825] focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation Menu */}
      <div className={`xl:hidden fixed inset-y-0 right-0 w-80 max-w-sm bg-white border-l border-gray-200 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        <div className="flex justify-between items-center px-5 py-5 border-b border-gray-100 bg-gray-50">
          <span className="font-montserrat font-bold text-[#052046] tracking-wide">Menu Portfolio</span>
          <button onClick={() => setIsOpen(false)} className="p-2 rounded-lg text-gray-400 hover:text-[#052046]">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="h-[calc(100vh-80px)] overflow-y-auto px-4 py-6 space-y-3 no-scrollbar">
          {navLinks.map((link) => (
            <div key={link.name}>
              {link.dropdown ? (
                <div>
                  <div className="w-full flex justify-between items-center py-2">
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`text-base font-semibold transition-colors ${location.pathname === link.path ? 'text-[#B31825]' : 'text-[#052046] hover:text-[#B31825]'
                        }`}
                    >
                      {link.name}
                    </Link>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                      className="p-2 text-[#052046] hover:text-[#B31825] focus:outline-none"
                    >
                      <ChevronDown className={`h-4 w-4 transform transition-transform duration-200 ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  <div className={`pl-4 border-l border-gray-100 space-y-2 mt-2 transition-all duration-300 overflow-hidden ${activeDropdown === link.name ? 'max-h-60' : 'max-h-0'
                    }`}>
                    {link.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.path}
                        onClick={() => setIsOpen(false)}
                        className="block py-1.5 text-sm text-gray-600 hover:text-[#B31825]"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block py-2 text-base font-semibold text-[#052046] hover:text-[#B31825]"
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}


        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 xl:hidden"
        />
      )}
    </nav>
  );
}
